import get from 'lodash/get'
import * as Sentry from '@sentry/browser'

import services from './config/services'

/**
 * A list of exception types we can safely ignore
 * @type {Array}
 */
const exceptionTypesToIgnore = ['ChunkLoadError']

/**
 * A list of TypeError values we can safely ignore
 * @type {Array}
 */
const typeErrorValuesToIgnore = ['Failed to fetch', 'cancelled', 'The request timed out']

/**
 * Determines whether we can safely ignore a given exception object
 *
 * @param {Object} exception
 * @param {String} exception.type The exception type
 * @param {String} exception.value The exception value
 * @returns {Boolean}
 */
const isFilterableError = ({type, value}) => {
  if (exceptionTypesToIgnore.includes(type)) {
    return true
  }
  if (type === 'TypeError' && typeErrorValuesToIgnore.includes(value)) {
    return true
  }
  return false
}

/**
 * All events captured by Sentry run through this function before being sent to
 * their servers. If we return `null`, the event will not be sent and therefore
 * not count against our quotas.
 *
 * @param {Object} event Sentry's event object
 * @returns {Object|null}
 * @see [Filtering Events](https://docs.sentry.io/error-reporting/configuration/filtering/?platform=browsernpm)
 */
export const beforeSend = (event) => {
  const shouldFilterError = get(event, 'exception.values', []).some(isFilterableError)
  return shouldFilterError ? null : event
}

/**
 * Initializes the Sentry library.
 */
Sentry.init({dsn: services.SENTRY_URL, beforeSend})

/**
 * An initialized Sentry Object. Exposes methods we can use to report and enrich
 * errors that occur in our application.
 *
 * @type  {Object}
 */
export default Sentry
